import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { Loader2, X } from "lucide-react";
import {
  CSSProperties,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type Size = "small" | "medium";

type Props = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "size"
> & {
  value: string;
  onChange: (value: string) => void;
  /** Optional callback fired when the clear (X) button is clicked. Defaults to onChange(""). */
  onClear?: () => void;
  /** Decorative glyph pinned inside the left edge; the text inset makes room for it. */
  icon?: ReactNode;
  /** Spinner in the trailing slot, for work the current value has already started. */
  loading?: boolean;
  /** Quiet trailing note, typically a keyboard shortcut (see Kbd). */
  hint?: ReactNode;
  size?: Size;
  /** The native input, for callers that focus or select its text. */
  inputRef?: Ref<HTMLInputElement>;
};

/*
  Sizes mirror Button — small/medium share the same height + rounded values, so
  Input and Button line up when placed side by side in a form row.
*/
const sizeClasses: Record<Size, string> = {
  small: "h-8 px-3 text-base",
  medium: "h-10 px-3 text-base",
};

const clearOffsetClasses: Record<Size, string> = {
  small: "right-2",
  medium: "right-3",
};

const iconOffsetClasses: Record<Size, string> = {
  small: "left-3",
  medium: "left-3",
};

const iconPaddingClasses: Record<Size, string> = {
  small: "pl-9",
  medium: "pl-10",
};

/** Gap between the end of the text and the hint sitting next to it. */
const HINT_GAP = 8;

/*
  The trailing slot holds one thing at a time, in this order: the spinner while
  a value is being worked on, the clear button once there is something to
  clear, and otherwise the hint. So the hint is only ever on screen next to the
  placeholder, and never competes with the clear button for the same corner.
*/
const Input = ({
  value,
  onChange,
  onClear,
  icon,
  loading = false,
  hint,
  className,
  type = "text",
  size = "medium",
  inputRef,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const hasValue = value.length > 0;
  const showClear = hasValue && !loading;
  const showHint = !!hint && !hasValue && !loading;

  // The hint is arbitrary content (and translated), so its width is measured
  // rather than guessed — that reserved inset is what keeps a long placeholder
  // from running underneath it.
  const hintRef = useRef<HTMLSpanElement>(null);
  const [hintWidth, setHintWidth] = useState(0);
  useLayoutEffect(() => {
    const element = hintRef.current;
    if (!element) {
      setHintWidth(0);
      return;
    }
    const observer = new ResizeObserver(() =>
      setHintWidth(element.offsetWidth),
    );
    observer.observe(element);
    setHintWidth(element.offsetWidth);
    return () => observer.disconnect();
  }, [showHint]);

  return (
    <div className="relative w-full">
      {icon && (
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute top-1/2 -translate-y-1/2 flex items-center text-neutral-500",
            iconOffsetClasses[size],
          )}
        >
          {icon}
        </span>
      )}
      <input
        {...rest}
        ref={inputRef}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // Utilities are !important here, so the hint's inset goes through a variable, not an inline padding.
        style={
          showHint
            ? ({
                "--input-hint-inset": `${hintWidth + HINT_GAP * 2}px`,
                ...rest.style,
              } as CSSProperties)
            : rest.style
        }
        className={cn(
          "w-full rounded-[20px] border border-neutral-600 bg-neutral-800 text-neutral-100 placeholder:text-neutral-500 outline-none",
          // The browser's own search clear button; Input draws its own.
          "[&::-webkit-search-cancel-button]:appearance-none",
          sizeClasses[size],
          icon && iconPaddingClasses[size],
          (showClear || loading) && "pr-9",
          showHint && "pr-(--input-hint-inset)",
          className,
        )}
      />
      {loading && (
        <span
          role="status"
          aria-label={t("loading")}
          className={cn(
            "pointer-events-none absolute top-1/2 -translate-y-1/2 flex size-5 items-center justify-center text-neutral-500",
            clearOffsetClasses[size],
          )}
        >
          <Loader2 className="size-4 animate-spin" />
        </span>
      )}
      {showClear && (
        <button
          type="button"
          aria-label={t("clear")}
          onClick={() => (onClear ? onClear() : onChange(""))}
          className={cn(
            "absolute top-1/2 -translate-y-1/2 flex size-5 items-center justify-center rounded-full text-neutral-400 hover:text-neutral-100 hover:bg-neutral-700 transition-colors",
            clearOffsetClasses[size],
          )}
        >
          <X className="size-4" />
        </button>
      )}
      {showHint && (
        <span
          ref={hintRef}
          className={cn(
            "pointer-events-none absolute top-1/2 -translate-y-1/2 flex items-center gap-x-1 opacity-70",
            clearOffsetClasses[size],
          )}
        >
          {hint}
        </span>
      )}
    </div>
  );
};

export default Input;
